import type { InstrumentId, Timeframe } from "@trader/shared/MarketData"
import type { AccountId, AmendOrder, ClockCommand, CreateAccount, OrderId, PlaceOrder, RunnerId, SetProtection } from "@trader/shared/Money"
import { ApiError, call } from "../../api.ts"
import { describeError, type ErrorMessage } from "./messages.ts"

// The paper-trading endpoints, through the typed client in api.ts (the only
// place the frontend talks HTTP). Each rejects with an ApiError.

const PAGE = 100

export const tradingApi = {
  list: () => call((client) => client.accounts.list()),
  create: (payload: CreateAccount) => call((client) => client.accounts.create({ payload })),
  reset: (id: AccountId) => call((client) => client.accounts.reset({ params: { id } })),
  end: (id: AccountId) => call((client) => client.accounts.end({ params: { id } })),
  /** docs/design/0015: an ended account, for good. */
  remove: (id: AccountId) => call((client) => client.accounts.remove({ params: { id } })),
  sessionSummary: (id: AccountId) => call((client) => client.accounts.sessionSummary({ params: { id } })),
  listClosedOrders: (id: AccountId, before: number | undefined) =>
    call((client) => client.accounts.listOrders({ params: { id }, query: { status: "closed", before, limit: PAGE } })),
  listFills: (id: AccountId, before: number | undefined) =>
    call((client) => client.accounts.listFills({ params: { id }, query: { before, limit: PAGE } })),
  placeOrder: (id: AccountId, payload: PlaceOrder) => call((client) => client.accounts.placeOrder({ params: { id }, payload })),
  cancelOrder: (id: AccountId, orderId: OrderId) => call((client) => client.accounts.cancelOrder({ params: { id, orderId } })),
  /** docs/design/0013: a working order at new prices, in place. */
  amendOrder: (id: AccountId, orderId: OrderId, payload: AmendOrder) =>
    call((client) => client.accounts.amendOrder({ params: { id, orderId }, payload })),
  /** docs/design/0013: a position's stop-loss and take-profit (null removes one). */
  setProtection: (id: AccountId, instrumentId: InstrumentId, payload: SetProtection) =>
    call((client) => client.accounts.setProtection({ params: { id, instrumentId }, payload })),
  clock: (id: AccountId, payload: ClockCommand) =>
    call((client) => {
      // One call per command shape: the client types each payload variant separately.
      switch (payload._tag) {
        case "Play":
          return client.accounts.clock({ params: { id }, payload })
        case "Pause":
          return client.accounts.clock({ params: { id }, payload })
        case "Step":
          return client.accounts.clock({ params: { id }, payload })
        case "Advance":
          return client.accounts.clock({ params: { id }, payload })
      }
    }),
  rename: (id: AccountId, name: string) => call((client) => client.accounts.rename({ params: { id }, payload: { name } })),
  listRunners: (id: AccountId) => call((client) => client.accounts.listRunners({ params: { id } })),
  pauseRunner: (id: AccountId, runnerId: RunnerId) => call((client) => client.accounts.pauseRunner({ params: { id, runnerId } })),
  resumeRunner: (id: AccountId, runnerId: RunnerId) => call((client) => client.accounts.resumeRunner({ params: { id, runnerId } })),
  stopRunner: (id: AccountId, runnerId: RunnerId, flatten: boolean) =>
    call((client) => client.accounts.stopRunner({ params: { id, runnerId }, payload: { flatten } })),
  runnerReport: (id: AccountId, runnerId: RunnerId) => call((client) => client.accounts.runnerReport({ params: { id, runnerId } })),
  /** Open time of the newest stored bar of a series (null: none stored). */
  latestStoredBar: (instrument: InstrumentId, timeframe: Timeframe) =>
    call((client) => client.candles.latest({ query: { instrument, timeframe } })).then(({ time }) => time)
}

/** The error's server tag ("OrderDenied"), or "UnknownError" for anything else. */
export const tagOf = (error: unknown): string => (error instanceof ApiError ? error.tag : "UnknownError")

/** What to tell the user about a failed request. */
export const messageFor = (error: unknown): ErrorMessage =>
  error instanceof ApiError ? describeError(error.tag, error.detail) : describeError("UnknownError", error)
